import type { TaxIdType } from '@stripe/stripe-js';
import type { Subscription, UserSubscription } from 'types';

/** Swiss QR-Bill debtor name max length. */
export const MAX_BILLING_NAME_LENGTH = 70;

/** Stripe Address Element address fields we care about for prefill. */
export type InvoiceBillingAddressDefaults = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country: string;
};

/** Prefill payload for invoice billing (Elements + local fields). */
export type InvoiceBillingDefaults = {
  name: string;
  address: InvoiceBillingAddressDefaults | null;
  taxId: {
    taxId: string;
    taxIdType?: TaxIdType;
    businessName?: string;
  } | null;
  billingEmails: string[];
};

export type InvoiceBillingSource = Partial<
  Pick<Subscription, 'payerEmails' | 'billingAddress' | 'taxId'>
> | null;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const pickFirstString = (...values: unknown[]): string => {
  for (const value of values) {
    const trimmed = asTrimmedString(value);
    if (trimmed) return trimmed;
  }
  return '';
};

/** Accept Address Element `value`, nested `{ address }`, or a flat address object. */
export const normalizeInvoiceBillingAddress = (
  raw: unknown,
  fallbackCountry: string,
): InvoiceBillingAddressDefaults | null => {
  const root = asRecord(raw);
  if (!root) return null;

  const nested = asRecord(root.address);
  const address = nested || root;

  const line1 = asTrimmedString(address.line1);
  const city = asTrimmedString(address.city);
  const postalCode = asTrimmedString(address.postal_code ?? address.postalCode);
  const country =
    asTrimmedString(address.country).toUpperCase() || fallbackCountry;

  // Need at least one concrete field beyond country to treat as "saved".
  if (!line1 && !city && !postalCode) return null;

  return {
    line1: line1 || null,
    line2: asTrimmedString(address.line2) || null,
    city: city || null,
    state: asTrimmedString(address.state) || null,
    postal_code: postalCode || null,
    country,
  };
};

export const normalizeInvoiceTaxId = (
  raw: unknown,
): InvoiceBillingDefaults['taxId'] => {
  const root = asRecord(raw);
  if (!root) return null;

  const taxId = pickFirstString(root.taxId, root.value, root.id);
  if (!taxId) return null;

  const taxIdType = pickFirstString(
    root.taxIdType,
    root.type,
    root.externalTaxIdType,
  );

  return {
    taxId,
    ...(taxIdType ? { taxIdType: taxIdType as TaxIdType } : {}),
    businessName: asTrimmedString(root.businessName) || undefined,
  };
};

export const normalizeInvoiceBillingEmails = (emails: unknown): string[] => {
  if (!Array.isArray(emails)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const entry of emails) {
    const email = asTrimmedString(entry).toLowerCase();
    if (!email || seen.has(email)) continue;
    seen.add(email);
    result.push(asTrimmedString(entry));
  }
  return result;
};

export type BuildInvoiceBillingDefaultsOptions = {
  source?: InvoiceBillingSource;
  /** Used when no saved debtor name exists (e.g. community name). */
  fallbackName?: string;
};

/**
 * Build Element/local field defaults from a subscription (or any compatible source).
 * Expects API camelCase: `billingAddress` (with nested `name`) and `taxId`.
 */
export const buildInvoiceBillingDefaults = ({
  source,
  fallbackName = '',
}: BuildInvoiceBillingDefaultsOptions = {}): InvoiceBillingDefaults => {
  const billingAddress = asRecord(source?.billingAddress);

  const name = pickFirstString(
    billingAddress?.name,
    billingAddress?.firstName && billingAddress?.lastName
      ? `${asTrimmedString(billingAddress.firstName)} ${asTrimmedString(
          billingAddress.lastName,
        )}`
      : '',
    fallbackName,
  ).slice(0, MAX_BILLING_NAME_LENGTH);

  const address = normalizeInvoiceBillingAddress(source?.billingAddress, '');

  return {
    name,
    address,
    taxId: normalizeInvoiceTaxId(source?.taxId),
    billingEmails: normalizeInvoiceBillingEmails(source?.payerEmails),
  };
};

/** Stable remount key so Stripe Elements pick up *saved* defaults when Redux loads. */
export const invoiceBillingDefaultsKey = (
  defaults: InvoiceBillingDefaults,
): string =>
  [
    defaults.name,
    defaults.address?.line1,
    defaults.address?.city,
    defaults.address?.postal_code,
    defaults.address?.country,
    defaults.taxId?.taxId,
    defaults.billingEmails.join(','),
  ].join('|');

export const getInvoiceBillingDefaultsFromSubscription = (
  subscription: UserSubscription | Subscription | null | undefined,
  options?: Omit<BuildInvoiceBillingDefaultsOptions, 'source'>,
): InvoiceBillingDefaults =>
  buildInvoiceBillingDefaults({
    ...options,
    source: subscription ?? null,
  });
